import { gql } from "@apollo/client";

import complianceProgramForReportAssessmentGroupFragment from "./compliance-program-for-report-assessment-group-fragment";
import complianceProgramReportQuestionFragment from "./compliance-program-report-question-fragment";
import complianceProgramTargetFragment from "./compliance-program-target-fragment";

export default gql`
  ${complianceProgramReportQuestionFragment}
  ${complianceProgramForReportAssessmentGroupFragment}
  ${complianceProgramTargetFragment}
  fragment ComplianceProgramReportAnswer on AssessmentAnswerInterface {
    id
    hasCalibrations
    hasCalibrationSuggestions
    ... on RangeAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on IndividualGoalAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on OverallGoalAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on IndividualRecognitionCoreValueAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on OverallRecognitionCoreValueAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on ResponsibilityAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
      roleId
    }
    ... on TalentIndicatorAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
    ... on CompetencyAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
      roleId
    }
    ... on CompetencyCriteriaAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
      roleId
    }
    ... on MultiChoiceAssessmentAnswerNode {
      id
      questionId
      choices
      comment
    }
    ... on TextAssessmentAnswerNode {
      id
      questionId
      textAnswer
      comment
    }
    ... on NpsAssessmentAnswerNode {
      id
      questionId
      integerAnswer
      comment
    }
  }
  fragment ComplianceProgramReportAssessment on AssessmentNode {
    id
    state
    responder {
      id
      name
      firstName
      avatar
    }
    target {
      ...ComplianceProgramTarget
    }
    answers(
      questionType: [
        range
        talent_indicator
        overall_goal
        overall_recognition_core_value
      ]
    ) {
      ...ComplianceProgramReportAnswer
    }
    roleAssessmentAvg
    selfRoleAssessmentAvg
    assessmentDelivery {
      id
      state
      canUpdate {
        permission
      }
      deliveryDatetime
      canRead {
        permission
      }
    }
    template {
      id
      organization {
        id
      }
    }
    assessmentDeliveryState
  }

  fragment ComplianceProgramPerformanceReportRow on PerformanceReviewReportRowNode {
    id
    canSuggestAnswer {
      permission
    }
    canCalibrateAnswer {
      permission
    }
    user {
      ...ComplianceProgramTarget
    }
    targetAddedToProgramOn
    position
    calibrationStatus
    roles {
      id
      title
      level
      careerTrack {
        id
        title
      }
    }
    assessment {
      ...ComplianceProgramReportAssessment
    }
  }

  fragment ComplianceProgramManagerEffectivenessReportRow on ManagerEffectivenessReportRowNode {
    manager {
      ...ComplianceProgramTarget
      directReports {
        totalCount
      }
    }
    assessments {
      totalCount
    }
    assessmentDelivery {
      id
    }
    questions {
      edges {
        node {
          question {
            id
          }
          avgAnswer
        }
      }
    }
  }

  query getComplianceProgramForReport(
    $complianceProgramId: Int!
    $isUpwardAssessmentReport: Boolean!
    $isPerformanceAssessmentReport: Boolean!
  ) {
    complianceProgram(pk: $complianceProgramId) {
      id
      title
      periodStartDate
      periodEndDate
      hasAccessToAppliesToUsers
      ongoing
      calibration
      canReadDetails {
        permission
      }
      performanceAssessmentTemplate
        @include(if: $isPerformanceAssessmentReport) {
        id
        assessmentType
        isOnlySelfAssessment
        delivery
        enableGoalAssessments
        goalAssessmentScale
        goalAssessmentScaleLabels
        goalAssessmentScaleStartsAt
        enableCoreValueAssessments
        coreValueAssessmentScale
        coreValueAssessmentScaleLabels
        coreValueAssessmentScaleStartsAt
        enableRoleBasedAssessments
        roleBasedAssessmentScale
        roleBasedAssessmentScaleLabels
        roleBasedAssessmentScaleStartsAt
        questionSets {
          edges {
            node {
              ...ComplianceProgramForReportAssessmentGroup
            }
          }
        }
        additionalQuestions {
          ... on OverallGoalAssessmentQuestionNode {
            id
            title
            description
            startValue
            endValue
            labels
            questionType
            responses
            labelDescriptions
            comment
          }
          ... on OverallRecognitionCoreValueAssessmentQuestionNode {
            id
            title
            description
            startValue
            endValue
            labels
            questionType
            responses
            labelDescriptions
            comment
          }
          ... on RangeAssessmentQuestionNode {
            id
          }
          ... on CompetencyAssessmentQuestionNode {
            id
          }
          ... on IndividualGoalAssessmentQuestionNode {
            id
          }
          ... on IndividualRecognitionCoreValueAssessmentQuestionNode {
            id
          }
          ... on CompetencyCriteriaAssessmentQuestionNode {
            id
          }
          ... on ResponsibilityAssessmentQuestionNode {
            id
          }
          ... on TalentIndicatorAssessmentQuestionNode {
            id
          }
          ... on MultiChoiceAssessmentQuestionNode {
            id
          }
          ... on TextAssessmentQuestionNode {
            id
          }
          ... on NpsAssessmentQuestionNode {
            id
          }
        }
      }
      managerAssessmentTemplate @include(if: $isUpwardAssessmentReport) {
        id
        assessmentType
        isOnlySelfAssessment
        delivery
        enableGoalAssessments
        goalAssessmentScale
        goalAssessmentScaleLabels
        goalAssessmentScaleStartsAt
        enableCoreValueAssessments
        coreValueAssessmentScale
        coreValueAssessmentScaleLabels
        coreValueAssessmentScaleStartsAt
        enableRoleBasedAssessments
        roleBasedAssessmentScale
        roleBasedAssessmentScaleLabels
        roleBasedAssessmentScaleStartsAt
        questionSets {
          edges {
            node {
              ...ComplianceProgramForReportAssessmentGroup
            }
          }
        }
        additionalQuestions {
          ... on OverallGoalAssessmentQuestionNode {
            id
            title
            description
            startValue
            endValue
            labels
            questionType
            responses
            labelDescriptions
            comment
          }
          ... on OverallRecognitionCoreValueAssessmentQuestionNode {
            id
            title
            description
            startValue
            endValue
            labels
            questionType
            responses
            labelDescriptions
            comment
          }
          ... on RangeAssessmentQuestionNode {
            id
          }
          ... on CompetencyAssessmentQuestionNode {
            id
          }
          ... on IndividualGoalAssessmentQuestionNode {
            id
          }
          ... on IndividualRecognitionCoreValueAssessmentQuestionNode {
            id
          }
          ... on CompetencyCriteriaAssessmentQuestionNode {
            id
          }
          ... on ResponsibilityAssessmentQuestionNode {
            id
          }
          ... on TalentIndicatorAssessmentQuestionNode {
            id
          }
          ... on MultiChoiceAssessmentQuestionNode {
            id
          }
          ... on TextAssessmentQuestionNode {
            id
          }
          ... on NpsAssessmentQuestionNode {
            id
          }
        }
      }
      performanceReportRows @include(if: $isPerformanceAssessmentReport) {
        edges {
          node {
            ...ComplianceProgramPerformanceReportRow
          }
        }
      }
      managerEffectivenessReport @include(if: $isUpwardAssessmentReport) {
        edges {
          node {
            ...ComplianceProgramManagerEffectivenessReportRow
          }
        }
      }
    }
  }
`;
